import { useState } from "react";
import { saveCropCycle } from "services/api";

export default function CropCyclePanel({ mac, cropCycle, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    transplant_date: "",
    estimated_flowering_start: "",
    estimated_flowering_end: "",
    harvest_date: "",
    notes: "",
  });

  const startEditing = () => {
    setForm({
      transplant_date: cropCycle?.transplant_date || "",
      estimated_flowering_start: cropCycle?.estimated_flowering_start || "",
      estimated_flowering_end: cropCycle?.estimated_flowering_end || "",
      harvest_date: cropCycle?.harvest_date || "",
      notes: cropCycle?.notes || "",
    });
    setError(null);
    setEditing(true);
  };

  const handleSave = async () => {
    if (!form.transplant_date) { setError("Transplant date is required."); return; }
    setSaving(true);
    setError(null);
    try {
      await saveCropCycle(mac, form);
      setEditing(false);
      onSaved?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-700">Crop Cycle</h3>
          <button
            className="text-sm text-blue-600 hover:underline"
            onClick={startEditing}
          >
            {cropCycle ? "Edit" : "Set up"}
          </button>
        </div>

        {cropCycle ? (
          <div className="space-y-1 text-sm text-gray-600">
            <div><span className="text-gray-400">Transplant:</span> {cropCycle.transplant_date}</div>
            {cropCycle.estimated_flowering_start && (
              <div>
                <span className="text-gray-400">Flowering:</span>{" "}
                {cropCycle.estimated_flowering_start} → {cropCycle.estimated_flowering_end}
              </div>
            )}
            {cropCycle.harvest_date && (
              <div><span className="text-gray-400">Harvest:</span> {cropCycle.harvest_date}</div>
            )}
            {cropCycle.in_flowering_window && (
              <div className="mt-2 px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                Currently in flowering safe period
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400">
            No crop cycle set. Add transplant and flowering dates to enable AWD phase tracking.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="font-semibold text-gray-700 mb-3">Set Crop Cycle</h3>
      <div className="space-y-3">
        <label className="block">
          <span className="text-xs text-gray-500">Transplant date *</span>
          <input
            type="date"
            className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
            value={form.transplant_date}
            onChange={e => setForm(f => ({ ...f, transplant_date: e.target.value }))}
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs text-gray-500">Flowering start</span>
            <input
              type="date"
              className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              value={form.estimated_flowering_start}
              onChange={e => setForm(f => ({ ...f, estimated_flowering_start: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray-500">Flowering end</span>
            <input
              type="date"
              className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              value={form.estimated_flowering_end}
              onChange={e => setForm(f => ({ ...f, estimated_flowering_end: e.target.value }))}
            />
          </label>
        </div>
        <label className="block">
          <span className="text-xs text-gray-500">Estimated harvest date</span>
          <input
            type="date"
            className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
            value={form.harvest_date}
            onChange={e => setForm(f => ({ ...f, harvest_date: e.target.value }))}
          />
        </label>
        <label className="block">
          <span className="text-xs text-gray-500">Notes</span>
          <textarea
            rows={2}
            className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          />
        </label>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex gap-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            onClick={() => setEditing(false)}
            className="px-4 py-1.5 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
